import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onSnapshot, FirestoreError, Unsubscribe, QueryDocumentSnapshot } from 'firebase/firestore';
import { getQuery, GetQueryProps } from 'services/api/firestore';
import { devlog } from 'utilities';
import { FetchState } from 'types';

type State = FetchState;

type Return<T> = {
  data: T[];
  docs: Array<QueryDocumentSnapshot<T>>;
  state: State;
  error: FirestoreError | null;
  unsubscribe: Unsubscribe | null;
};

/**
 * Subscribes to live data using Firestore snapshot.
 * For best performance and prevent infinite-loops make sure to memoize props object.
 * Hook will automatically unsubscribe on component unmount, but cam be done manually
 * if necessary by calling `unsubscribe()`.
 *
 * @example Passing static props - query can be defined as const outside of component to avoid need to memoize
 *	type YourSchema = {}; // Data type you expect to receive, or use Firebase's DocumentData instead.
 * 	const testQuery: GetQueryProps<YourExpectedSchema> = {
 * 			collectionPath: 'test',
 * 			ordering: [{ field: 'field2', type: 'asc' }],
 * 			filters: [{ attribute: 'field2', operator: '<=', value: 2 }],
 * 			snapshotConstraint: { type: 'startAfter', value: 1 },
 * 			limit: 10,
 * 	};
 *
 * 	// You will mostly only need { data, state, error }.
 * 	const { data, state, error, unsubscribe, snapshot } = useLiveData(testQuery);
 *
 * 	@example Returning null to avoid fetches until dynamic data arrives
 * 	const privateDocsQuery: GetQueryProps | null = useMemo(() => {
 * 		if (!instance?.firebaseUser?.uid) return null; // Will skip fetching until user id is ready.
 *
 * 		return {
 * 			collectionPath: `usersWeb/${instance?.firebaseUser?.uid}/private`,
 * 		};
 * 	}, [instance?.firebaseUser?.uid]);
 *
 * 	const { docs: privateDocs, state: privateDocsState } = useLiveData(privateDocsQuery);
 *
 * @see getQuery
 * @param memoizedProps Query props. Leave null to skip fetches until required data arrives. IMPORTANT! Memoize props to prevent infinite loop.
 */
export const useLiveData = <Schema>(
  memoizedProps: GetQueryProps<Schema> | null,
): Return<Schema> => {
  const [state, setState] = useState<State>('loading');
  const [docs, setDocs] = useState<Array<QueryDocumentSnapshot<Schema>>>([]);
  const [error, setError] = useState<FirestoreError | null>(null);

  const query = useMemo(
    () => (memoizedProps ? getQuery<Schema>(memoizedProps) : null),
    [memoizedProps],
  );
  const data = useMemo(() => docs?.map((doc) => doc.data()) || [], [docs]);

  const unsubscribe = useRef<Unsubscribe | null>(null);

  const subscribe = useCallback(() => {
    if (!query) return null; // null is expected by `unsubscribe` ref

    return onSnapshot(
      query,
      (querySnapshot) => {
        setDocs(querySnapshot.docs);
        setState('success');
        setError(null);
      },
      (firestoreError) => {
        devlog('error', firestoreError);
        setState('error');
        setError(firestoreError);
      },
      () => {
        setState('success');
        setError(null);
      },
    );
  }, [query]);

  useEffect(() => {
    const unsubscribeSnapshot = subscribe();
    unsubscribe.current = unsubscribeSnapshot;

    return () => {
      unsubscribeSnapshot && unsubscribeSnapshot();
    };
  }, [subscribe]);

  return {
    data,
    docs,
    state,
    error,
    unsubscribe: unsubscribe.current,
  };
};

/**
 * Get combined state for multiple requests with precedence: error > pending > loading > success
 * @param states
 */
export function getCombinedState(...states: State[]): State {
  if (states.includes('error')) return 'error';
  if (states.includes('pending')) return 'pending';
  if (states.includes('loading')) return 'loading';
  return 'success';
}

// Types for external usage.
export type LiveDataProps<Schema> = GetQueryProps<Schema>;
export type LiveData<T> = Return<T>;
export type LiveDataState = State;
